import React, { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { useSnackbar } from "notistack";
import { Dialog, DialogActions, DialogContent } from "@mui/material";
import { Box, Button, Typography } from "@curves/ui-base";
import { UploadBox } from "@curves/ui-shared";
import GradientColorHeader from "@icoach/components/dialog/GradientColorHeader";
import useInvoiceApi from "@apis/useInvoiceApi";
import useShowPDF from "@util/hook/useShowPDF";
import { isFunction } from "@util/utils";

/** 與後端一致：InvoiceTriplicateUniformController 僅接受 ImageHelper 圖檔（jpg／jpeg／png／gif／bmp），不含 PDF、HEIC */
const ALLOWED_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "bmp"];

/**
 * 上傳三聯折讓證明單。
 * sourceData：orderID、orderReturnID 必填；可選 onSubmit 覆寫上傳。
 */
const UploadTriplicateAllowanceCertificateDialog = props => {
	const { className, open, sourceData = {}, onClose, refresh } = props;
	const { orderID, orderReturnID, onSubmit } = sourceData;
	const { postInvoiceAllowanceTriplicateUniformReturnUploadApi } = useInvoiceApi();
	const { enqueueSnackbar } = useSnackbar();
	const showPDF = useShowPDF();
	const [fileData, setFileData] = useState(null);
	const [isSubmitting, setSubmitting] = useState(false);

	// UploadBox 回傳 { file, ... } 或舊版直接傳 File
	const uploadFile = useMemo(() => (fileData instanceof File ? fileData : (fileData?.file ?? null)), [fileData]);

	const canSubmit = useMemo(() => !!uploadFile && !isSubmitting, [uploadFile, isSubmitting]);

	// 關閉時重置，避免下次開啟仍帶舊檔
	useEffect(() => {
		if (!open) {
			setFileData(null);
			setSubmitting(false);
		}
	}, [open]);

	const handleFileChange = payload => {
		setFileData(payload || null);
	};

	const handleSubmit = async () => {
		if (!canSubmit || !uploadFile) return;

		if (isFunction(onSubmit)) {
			setSubmitting(true);
			try {
				await onSubmit({ orderID, orderReturnID, fileData: { file: uploadFile } });
				if (isFunction(refresh)) refresh();
				onClose();
			} finally {
				setSubmitting(false);
			}
			return;
		}

		if (!orderID || orderReturnID == null || Number(orderReturnID) <= 0) {
			enqueueSnackbar("缺少訂單或退單資料，無法上傳", { variant: "error" });
			return;
		}

		setSubmitting(true);
		try {
			const resp = await postInvoiceAllowanceTriplicateUniformReturnUploadApi(orderID, orderReturnID, uploadFile);
			if (resp) {
				if (resp.path) {
					showPDF({ open: true, pdfUrl: resp.path });
				}
				if (isFunction(refresh)) refresh();
				onClose();
			}
		} finally {
			setSubmitting(false);
		}
	};

	if (!open) return null;

	return (
		<Dialog className={clsx("simple-content", "dialog", className)} PaperProps={{ className: "wx-65rem" }} open={open} fullWidth>
			<GradientColorHeader onClose={onClose}>上傳三聯式折讓證明單</GradientColorHeader>
			<DialogContent>
				<Box className="mb-3">
					<Typography className="mb-1">請上傳證明單第三聯與第四聯的圖片檔，確認預覽內容後再送出。</Typography>
					<Typography variant="body2" className="font-color-3 mb-0">
						僅支援 JPG、PNG、GIF、BMP，不支援 PDF 與 HEIC／HEIF；
						<br />
						iPhone 請改「最相容」或匯出 JPEG／PNG。
					</Typography>
				</Box>
				<UploadBox fileExtensions={ALLOWED_EXTENSIONS} onFileChange={handleFileChange} previewHeight="48rem" />
			</DialogContent>
			<DialogActions>
				<Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
					送出
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default UploadTriplicateAllowanceCertificateDialog;
